import { analyticsService as businessAnalyticsService } from './index-business';
import { analyticsService as studentAnalyticsService } from './index-student';
import { PostHogAdapter } from './adapters/PostHogAdapter';
import { getAnalyticsConfig, logAnalyticsConfig } from './config';

export type AppType = 'business' | 'student';

class ConsentChecker {
  private static STORAGE_KEY = 'voopty_consent_preferences';

  static canTrackAnalytics(): boolean {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return false;

      const consent = JSON.parse(stored);
      return consent?.analytics === true;
    } catch (error) {
      return false;
    }
  }

  static needsConsent(): boolean {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return !stored;
    } catch (error) {
      return true;
    }
  }
}

class AnalyticsInitializer {
  private initializationAttempted = false;

  private isLocalhost(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  private log(message: string, ...args: any[]): void {
    if (this.isLocalhost()) {
      console.log(message, ...args);
    }
  }

  private logError(message: string, ...args: any[]): void {
    if (this.isLocalhost()) {
      console.error(message, ...args);
    }
  }

  private async initializeService(appType: AppType) {
    const config = getAnalyticsConfig(appType);
    const analyticsService = appType === 'business' ? businessAnalyticsService : studentAnalyticsService;

    this.log(`🔧 Analytics initialization for ${appType} app:`, {
      enabled: config.enabled,
      token: config.token ? `${config.token.substring(0, 8)}...` : 'NO TOKEN',
      hostname: window.location.hostname,
      debug: config.debug
    });

    if (ConsentChecker.needsConsent()) {
      this.log('⏳ Waiting for user consent... Analytics will initialize after consent is granted');
      this.setupConsentListener(appType);
      return;
    }

    if (!ConsentChecker.canTrackAnalytics()) {
      this.log('🚫 Analytics disabled by user consent');
      return;
    }

    if (!config.enabled) {
      this.log(`📊 Analytics disabled in config for ${appType} app`);
      return;
    }

    if (!config.apiKey) {
      this.logError(`❌ No PostHog API key configured for ${appType} app`);
      return;
    }

    try {
      if (this.isLocalhost()) {
        logAnalyticsConfig(appType);
      }

      const adapters = [new PostHogAdapter()];

      this.log(`🚀 Initializing ${adapters.length} adapter(s) for ${appType}...`);
      await analyticsService.initialize(adapters);

      this.log(`✅ Analytics for ${appType} app initialized successfully`);

      if (__DEV__ && this.isLocalhost()) {
        window[`${appType}Analytics`] = analyticsService;
        this.log(`🧪 ${appType} analytics available as window.${appType}Analytics`);

        setTimeout(() => {
          this.log(`📊 ${appType} analytics status:`, analyticsService.getStatus?.());
          this.log(`🔌 ${appType} adapter status:`, analyticsService.getAdapterStatus?.());
        }, 2000);
      }

    } catch (error) {
      this.logError(`❌ Analytics initialization error for ${appType}:`, error);
    }
  }

  private setupConsentListener(appType: AppType) {
    const handleConsentChange = (event: CustomEvent) => {
      const consent = event.detail;
      this.log(`🔄 Consent changed for ${appType} app:`, consent);

      if (consent.analytics && !this.initializationAttempted) {
        this.initializationAttempted = true;
        this.log(`✅ Analytics consent granted for ${appType}, initializing...`);

        setTimeout(() => {
          if (appType === 'business') {
            this.initializeBusiness();
          } else {
            this.initializeStudent();
          }
        }, 500);
      }
    };

    const handleInitializeAnalytics = (event: CustomEvent) => {
      const detail = event.detail;
      this.log(`🎯 Direct analytics initialization for ${appType}:`, detail);

      if (!this.initializationAttempted) {
        this.initializationAttempted = true;

        setTimeout(() => {
          if (appType === 'business') {
            this.initializeBusiness();
          } else {
            this.initializeStudent();
          }
        }, 500);
      }
    };

    if (!window._analyticsConsentListener) {
      window.addEventListener('consentChanged', handleConsentChange);
      window.addEventListener('initializeAnalytics', handleInitializeAnalytics);
      window._analyticsConsentListener = true;
      this.log(`👂 Consent listeners setup for ${appType} app`);
    }
  }

  private setupErrorHandlers(appType: AppType) {
    const config = getAnalyticsConfig(appType);

    if (!config.enabled || !config.trackErrors) {
      this.log(`⚠️ Error tracking disabled for ${appType} app`);
      return;
    }

    const analyticsService = appType === 'business' ? businessAnalyticsService : studentAnalyticsService;

    this.log(`🛡️ Setting up error handlers for ${appType} app`);

    window.addEventListener('error', (event) => {
      if (ConsentChecker.canTrackAnalytics()) {
        analyticsService.track('JavaScript Error', {
          error_message: event.message,
          error_filename: event.filename,
          error_line: event.lineno,
          error_column: event.colno,
          error_stack: event.error?.stack,
          app_type: appType
        });
      }
    });

    window.addEventListener('unhandledrejection', (event) => {
      if (ConsentChecker.canTrackAnalytics()) {
        analyticsService.track('Unhandled Promise Rejection', {
          error_message: event.reason?.message || event.reason,
          error_stack: event.reason?.stack,
          app_type: appType
        });
      }
    });
  }

  private handleRenderError(error: Error, appType: AppType, component: string) {
    const config = getAnalyticsConfig(appType);
    const analyticsService = appType === 'business' ? businessAnalyticsService : studentAnalyticsService;

    if (config.trackErrors && ConsentChecker.canTrackAnalytics()) {
      analyticsService.track('Render Error', {
        error_message: error.message,
        error_stack: error.stack,
        component: component,
        app_type: appType
      });
    }
  }

  public async initializeBusiness(): Promise<void> {
    this.log('🚀 Starting business analytics initialization...');
    await this.initializeService('business');
    this.setupErrorHandlers('business');
  }

  public async initializeStudent(): Promise<void> {
    this.log('🚀 Starting student analytics initialization...');
    await this.initializeService('student');
    this.setupErrorHandlers('student');
  }

  public wrapRenderWithErrorHandling(
    renderFn: () => void,
    appType: AppType,
    component: string = 'App'
  ): () => void {
    return () => {
      try {
        renderFn();
      } catch (error) {
        this.logError(`❌ Render error in ${component}:`, error);
        this.handleRenderError(error as Error, appType, component);
        throw error;
      }
    };
  }
}

export const analyticsInitializer = new AnalyticsInitializer();

export const initializeAnalytics = () => analyticsInitializer.initializeStudent();
export const initializeAnalyticsBiz = () => analyticsInitializer.initializeBusiness();

export const wrapRenderWithErrorHandling = (
  renderFn: () => void,
  appType: AppType,
  component?: string
) => analyticsInitializer.wrapRenderWithErrorHandling(renderFn, appType, component);

window.initializeAnalyticsAfterConsent = () => {
  if (window.location.hostname === 'localhost') {
    console.log('🎯 Manual analytics initialization triggered');
  }
  initializeAnalytics();
};
